import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  companies: [],
  cachedPages: {}, // Store companies by page number: { 1: [...], 2: [...], etc }
  pagination: {
    current: 1,
    pageSize: 20,
    total: 0,
  },
  searchQuery: "",
  loading: false,
  error: null,
};

const adminCompanySlice = createSlice({
  name: "adminCompany",
  initialState,
  reducers: {
    // Set companies for a specific page
    setCompaniesPage: (state, action) => {
      const { page, data, pagination } = action.payload;
      state.cachedPages[page] = data;
      state.companies = data;
      if (pagination) {
        state.pagination = pagination;
      }
      state.loading = false;
      state.error = null;
    },
    // Get companies from cache or mark as loading
    getCompaniesPage: (state, action) => {
      const page = action.payload;
      if (state.cachedPages[page]) {
        state.companies = state.cachedPages[page];
        state.loading = false;
      } else {
        state.loading = true;
      }
    },
    // Clear all cached pages (used when search changes or data is updated)
    clearCompaniesCache: (state) => {
      state.cachedPages = {};
      state.companies = [];
      state.pagination = {
        current: 1,
        pageSize: state.pagination.pageSize, // preserve current pageSize instead of resetting to default
        total: 0,
      };
    },
    setPageSize: (state, action) => {
      state.pagination.pageSize = action.payload;
      state.pagination.current = 1;
      state.cachedPages = {}; // cached data is per-page-size, so invalidate on change
    },
    // Update a single company in cache (optimistic update)
    updateCompanyInCache: (state, action) => {
      const { companyId, updates } = action.payload;

      // Update in current companies array
      const currentIndex = state.companies.findIndex(
        (c) => c._id === companyId
      );
      if (currentIndex !== -1) {
        state.companies[currentIndex] = {
          ...state.companies[currentIndex],
          ...updates,
        };
      }

      // Update in all cached pages
      Object.keys(state.cachedPages).forEach((pageNum) => {
        const pageCompanies = state.cachedPages[pageNum];
        const cachedIndex = pageCompanies.findIndex((c) => c._id === companyId);
        if (cachedIndex !== -1) {
          state.cachedPages[pageNum][cachedIndex] = {
            ...pageCompanies[cachedIndex],
            ...updates,
          };
        }
      });
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
      // Clear cache when search changes
      state.cachedPages = {};
      state.pagination.current = 1;
    },
    setCurrentPage: (state, action) => {
      state.pagination.current = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
  },
});

export const {
  setCompaniesPage,
  getCompaniesPage,
  clearCompaniesCache,
  updateCompanyInCache,
  setSearchQuery,
  setCurrentPage,
  setPageSize,
  setLoading,
  setError,
} = adminCompanySlice.actions;

export default adminCompanySlice.reducer;
